import { useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { MRT_ColumnDef } from 'material-react-table';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Columns3, Eye, RotateCcw, Search, SlidersHorizontal } from 'lucide';

// common
import Icon from 'common/components/Icon';
import MuiTable from 'common/components/MuiTable';
import IconButtonTooltip from 'common/components/IconButtonTooltip';
import useIsParent from 'common/hook/useIsParent';
import useIsGuest from 'common/hook/useIsGuest';
import { useAppDispatch } from '@redux/store';
import { ROUTES_PATH } from 'layout/constants';
import { KIOS_STORAGE } from 'api/API_App';

// screen
import type { QsSupplier } from 'modules/quick-search/types';
import { saveSupplierExtras } from 'modules/suppliers/helpers/supplierExtras';
import KiosImage from 'modules/quick-search/components/KiosImage';
import { hiddenSupplierCount } from 'modules/quick-search/helpers/guestLimit';
import { handleGuestLogin } from 'modules/quick-search/helpers/guestLogin';
import { useQuickSearchProvinces } from 'modules/quick-search/services/useQuickSearchTaxonomy';
import GuestLoginCta from 'modules/quick-search/components/GuestLoginCta';

interface Props {
  suppliers: QsSupplier[]; // toàn bộ NCC của nhóm (đã tải), lọc/tìm/phân trang client-side
  isLoading: boolean;
  totalAvailable?: number; // meta.total của BE — khách bị cắt còn 3 nên cần biết còn bao nhiêu
}

const DEFAULT_PER_PAGE = 20;

// Giới hạn chiều cao menu Tỉnh/Xã (Hà Nội tới 126 phường/xã) → hiện ~7 mục rồi cuộn.
const SELECT_MENU_PROPS = { PaperProps: { sx: { maxHeight: 300 } } } as const;

const composeAddress = (s: QsSupplier): string =>
  [s.addressDetail, s.wardName, s.districtName, s.cityName].filter(Boolean).join(', ');

const storageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  return `${(KIOS_STORAGE || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
};

/** Cột có thể ẩn/hiện qua nút "Column". */
const TOGGLEABLE = [
  { id: 'operationCode', labelId: 'quickSearch.columns.code' },
  { id: 'name', labelId: 'quickSearch.columns.name' },
  { id: 'address', labelId: 'quickSearch.columns.address' },
  { id: 'typeName', labelId: 'quickSearch.columns.type' },
  { id: 'description', labelId: 'quickSearch.columns.description' },
  { id: 'image', labelId: 'quickSearch.columns.image' },
];

/** UC2 — bảng HSNL NCC theo NMH: Tìm kiếm + Bộ lọc + Column, phân trang. Bấm xem → chi tiết (UC3). */
const SupplierResultTable: FC<Props> = ({ suppliers, isLoading, totalAvailable }) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const isParent = useIsParent();
  const isGuest = useIsGuest();
  const dispatch = useAppDispatch();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Lọc theo địa bàn: province/ward là TÊN, khớp exact cityName/wardName của NCC (đúng danh mục KIOS).
  const [filters, setFilters] = useState({ province: '', ward: '' });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [ctaOpen, setCtaOpen] = useState(false);

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [columnAnchor, setColumnAnchor] = useState<HTMLElement | null>(null);

  const { data: provinces = [] } = useQuickSearchProvinces();
  // Xã của tỉnh đang chọn (cascade). Khớp theo tên tỉnh vì name = cityName của NCC.
  const wardOptions = useMemo(
    () => provinces.find((p) => p.name === filters.province)?.wards ?? [],
    [provinces, filters.province]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (q && !`${s.name} ${s.operationCode}`.toLowerCase().includes(q)) return false;
      if (filters.province && s.cityName !== filters.province) return false;
      if (filters.ward && s.wardName !== filters.ward) return false;
      return true;
    });
  }, [suppliers, search, filters]);

  const total = filtered.length;
  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  // Khách chỉ nhận 3 bản ghi; meta.total cho biết nhóm còn bao nhiêu HSNL nữa.
  const hiddenForGuest = hiddenSupplierCount(isGuest, suppliers.length, totalAvailable);

  const applySearch = () => {
    setSearch(searchInput);
    setPage(1);
  };
  const resetAll = () => {
    setSearchInput('');
    setSearch('');
    setFilters({ province: '', ward: '' });
    setPage(1);
  };
  const setFilter = (patch: Partial<typeof filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const allColumns: MRT_ColumnDef<QsSupplier>[] = [
    {
      id: 'rowNumber',
      header: 'STT',
      Header: () => <FormattedMessage id="stt" />,
      Cell: ({ row }) => (page - 1) * perPage + row.index + 1,
      size: 60,
    },
    {
      accessorKey: 'operationCode',
      id: 'operationCode',
      header: 'code',
      Header: () => <FormattedMessage id="quickSearch.columns.code" />,
      size: 190,
    },
    {
      accessorKey: 'name',
      id: 'name',
      header: 'name',
      Header: () => <FormattedMessage id="quickSearch.columns.name" />,
      size: 220,
    },
    {
      id: 'address',
      header: 'address',
      Header: () => <FormattedMessage id="quickSearch.columns.address" />,
      size: 220,
      accessorFn: (row) => composeAddress(row),
      Cell: ({ row }) => composeAddress(row.original) || '-',
    },
    {
      accessorKey: 'typeName',
      id: 'typeName',
      header: 'type',
      Header: () => <FormattedMessage id="quickSearch.columns.type" />,
      size: 160,
      Cell: ({ renderedCellValue }) => renderedCellValue || '-',
    },
    {
      accessorKey: 'description',
      id: 'description',
      header: 'description',
      Header: () => <FormattedMessage id="quickSearch.columns.description" />,
      size: 200,
      Cell: ({ renderedCellValue }) => renderedCellValue || '-',
    },
    {
      accessorKey: 'image',
      id: 'image',
      header: 'image',
      Header: () => <FormattedMessage id="quickSearch.columns.image" />,
      size: 120,
      enableSorting: false,
      Cell: ({ row: { original } }) => (
        <KiosImage
          src={storageUrl(original.image)}
          alt={original.name}
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: 1,
            display: 'block',
            margin: '0 auto',
          }}
        />
      ),
    },
  ];

  const columns = allColumns.filter((c) => !hidden[c.id as string]);

  return (
    <Box>
      {/* Thanh công cụ: Tìm kiếm + Bộ lọc + Làm mới + Column */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 500 }}>
          <FormattedMessage id="quickSearch.searchLabel" />
        </Typography>
        <TextField
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applySearch()}
          placeholder={formatMessage({ id: 'quickSearch.searchPlaceholder' })}
          sx={{ minWidth: 160, flex: '1 1 160px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon={Search} size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" startIcon={<Icon icon={Search} size={16} />} onClick={applySearch}>
          <FormattedMessage id="quickSearch.search" />
        </Button>
        <Button
          variant="outlined"
          startIcon={<Icon icon={SlidersHorizontal} size={16} />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
        >
          <FormattedMessage id="quickSearch.filter" />
        </Button>
        <Tooltip title={<FormattedMessage id="quickSearch.refresh" />}>
          <Button variant="outlined" aria-label="Làm mới" onClick={resetAll} sx={{ minWidth: 0, px: 1.25 }}>
            <Icon icon={RotateCcw} />
          </Button>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<Icon icon={Columns3} size={16} />}
          onClick={(e) => setColumnAnchor(e.currentTarget)}
        >
          <FormattedMessage id="quickSearch.column" />
        </Button>
      </Box>

      {/* Popover Bộ lọc: Tỉnh/TP → Phường/Xã (cascade) / phân loại NCC */}
      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ p: 2, width: 300 }} gap={1.5}>
          <TextField
            select
            size="small"
            label={formatMessage({ id: 'quickSearch.filterProvince' })}
            value={filters.province}
            onChange={(e) => setFilter({ province: e.target.value, ward: '' })}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            <MenuItem value="">
              <FormattedMessage id="quickSearch.all" />
            </MenuItem>
            {provinces.map((p) => (
              <MenuItem key={p.id} value={p.name}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={formatMessage({ id: 'quickSearch.filterWard' })}
            value={filters.ward}
            onChange={(e) => setFilter({ ward: e.target.value })}
            disabled={!filters.province}
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            <MenuItem value="">
              <FormattedMessage id="quickSearch.all" />
            </MenuItem>
            {wardOptions.map((w) => (
              <MenuItem key={w.id} value={w.name}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
          <Button size="small" onClick={() => setFilter({ province: '', ward: '' })}>
            <FormattedMessage id="quickSearch.clearFilter" />
          </Button>
        </Stack>
      </Popover>

      {/* Menu ẩn/hiện cột */}
      <Menu anchorEl={columnAnchor} open={!!columnAnchor} onClose={() => setColumnAnchor(null)}>
        <MenuList dense>
          {TOGGLEABLE.map((c) => (
            <MenuItem key={c.id} onClick={() => setHidden((h) => ({ ...h, [c.id]: !h[c.id] }))}>
              <FormControlLabel
                sx={{ pointerEvents: 'none' }}
                control={<Checkbox size="small" checked={!hidden[c.id]} />}
                label={formatMessage({ id: c.labelId })}
              />
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <MuiTable<QsSupplier>
        data={paged}
        columns={columns}
        state={{ isLoading }}
        enableRowActions={!isParent}
        renderRowActions={({ row: { original } }) => [
          <IconButtonTooltip
            key="view"
            tooltipProps={{ title: <FormattedMessage id="quickView" /> }}
            aria-label="Xem nhanh"
            sx={isGuest ? { color: 'text.disabled' } : undefined}
            onClick={() => {
              // Khách không được xem chi tiết HSNL (docx 07.08 [85]) → mời đăng nhập.
              if (isGuest) {
                setCtaOpen(true);
                return;
              }
              // Ảnh/mô tả/phân loại chỉ có ở list → lưu localStorage theo id để chi tiết đọc lại
              // được kể cả sau refresh (không phụ thuộc navigation state).
              saveSupplierExtras(original);
              navigate(ROUTES_PATH.quickSearch.detail(original.id), {
                state: { from: 'quickSearch' },
              });
            }}
          >
            <Icon icon={Eye} />
          </IconButtonTooltip>,
        ]}
        // Khách luôn nhận đúng 3 bản ghi từ BE nên không có gì để phân trang (Figma 7237-28440).
        paginationProps={
          isGuest
            ? undefined
            : {
                count: total,
                page,
                rowsPerPage: perPage,
                onPageChange: setPage,
                onRowsPerPageChange: (v: number) => {
                  setPerPage(v);
                  setPage(1);
                },
              }
        }
      />

      {/* Khách còn HSNL chưa xem được → mời đăng nhập/đăng ký (Figma 7237-28440) */}
      {hiddenForGuest > 0 && (
        <Stack alignItems="center" gap={1.5} sx={{ py: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <FormattedMessage id="quickSearch.guestMoreHint" />
          </Typography>
          <Button variant="contained" color="error" onClick={() => handleGuestLogin(dispatch)}>
            <FormattedMessage id="quickSearch.guestMoreAction" />
          </Button>
        </Stack>
      )}

      <GuestLoginCta open={ctaOpen} onClose={() => setCtaOpen(false)} />
    </Box>
  );
};

export default SupplierResultTable;
